import { useEffect, useId, useRef, useState, type ReactNode } from "react";

import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { normalizeWebsite } from "@/lib/domain";
import { clientsService, type ResolveResult } from "@/services/clientsService";

/**
 * Create-only fields of the New client dialog (feature C, design §6; R9, R19).
 * Controlled: the parent (ClientFormDialog, F4) owns the value and gates Save
 * on onValidityChange. The website is previewed locally and resolved against
 * the server 400 ms after the last keystroke; the fields are valid only after
 * a successful resolve for the website currently in the field.
 */
export type NewClientFieldsValue = {
  website: string;
  /** null = nothing chosen yet (no default, D5). */
  services: "seo" | "ads" | "both" | null;
  /** null = nothing chosen yet (no default, D5). */
  approval_required: boolean | null;
  confirm_ads_armed: boolean;
};

export interface NewClientFieldsProps {
  value: NewClientFieldsValue;
  onChange: (next: NewClientFieldsValue) => void;
  onValidityChange: (valid: boolean) => void;
  /** Where F4 mounts StaffMemberPicker. */
  membersSlot?: ReactNode;
}

const RESOLVE_DEBOUNCE_MS = 400;
const GENERIC_RESOLVE_ERROR = "Couldn't check this website. Try again in a moment.";

type ResolveStatus = "idle" | "loading" | "ok" | "error";

/** The first 422 message from FastAPI's detail list, without pydantic's prefix. */
function resolveErrorMessage(err: unknown): string {
  const response = (err as { response?: { status?: number; data?: { detail?: unknown } } })
    ?.response;
  if (response?.status === 422) {
    const detail = response.data?.detail;
    const first = Array.isArray(detail) ? (detail[0] as { msg?: unknown }) : undefined;
    const msg =
      typeof first?.msg === "string" ? first.msg : typeof detail === "string" ? detail : "";
    if (msg) return msg.replace(/^Value error,\s*/, "");
  }
  return GENERIC_RESOLVE_ERROR;
}

function previewCopy(preview: ResolveResult): string {
  const { tenant, workspace } = preview.will_create;
  if (tenant && workspace) return "New client: tenant and workspace will be created.";
  if (!workspace && preview.workspace) {
    return tenant
      ? `Existing workspace '${preview.workspace.name}' will be reused; a new tenant will be created.`
      : `Existing workspace '${preview.workspace.name}' will be reused.`;
  }
  return "Existing tenant will be reused; a new workspace will be created.";
}

export function NewClientFields({
  value,
  onChange,
  onValidityChange,
  membersSlot,
}: NewClientFieldsProps) {
  const ids = useId();
  const [preview, setPreview] = useState<ResolveResult | null>(null);
  const [status, setStatus] = useState<ResolveStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  // Latest value, and the website the last resolve was requested for, so a
  // late answer for an older website never wins (out-of-order responses).
  const valueRef = useRef(value);
  valueRef.current = value;
  const requestedRef = useRef("");

  const site = value.website.trim();
  const normalized = site ? normalizeWebsite(site) : "";

  useEffect(() => {
    requestedRef.current = site;
    setPreview(null);
    setError(null);
    if (!site) {
      setStatus("idle");
      return;
    }
    setStatus("loading");
    const timer = setTimeout(() => {
      clientsService
        .resolveClient(site)
        .then((result) => {
          if (requestedRef.current !== site) return;
          setPreview(result);
          setStatus("ok");
          if (!result.ads_armed && valueRef.current.confirm_ads_armed) {
            onChange({ ...valueRef.current, confirm_ads_armed: false });
          }
        })
        .catch((err: unknown) => {
          if (requestedRef.current !== site) return;
          setError(resolveErrorMessage(err));
          setStatus("error");
        });
    }, RESOLVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // onChange is read at call time; only a new website re-resolves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site]);

  const armed = status === "ok" && preview?.ads_armed === true;
  const valid =
    status === "ok" &&
    preview !== null &&
    preview.conflict === null &&
    value.services !== null &&
    value.approval_required !== null &&
    (!armed || value.confirm_ads_armed);

  useEffect(() => {
    onValidityChange(valid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valid]);

  const approvalValue =
    value.approval_required === null ? "" : value.approval_required ? "yes" : "no";

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <Label htmlFor={`${ids}-website`} className="text-xs text-muted-foreground">
          Website *
        </Label>
        <Input
          id={`${ids}-website`}
          value={value.website}
          placeholder="example.com"
          onChange={(e) =>
            // A new website drops any earlier ads-armed confirmation.
            onChange({ ...value, website: e.target.value, confirm_ads_armed: false })
          }
        />
        {normalized && (
          <p className="text-xs text-muted-foreground">{`Will connect to ${normalized}`}</p>
        )}
        {status === "ok" && preview && !preview.conflict && (
          <p className="text-xs text-muted-foreground">{previewCopy(preview)}</p>
        )}
        {status === "ok" && preview?.conflict && (
          <p role="alert" className="text-xs text-destructive">
            {preview.conflict.message}
          </p>
        )}
        {status === "error" && error && (
          <p role="alert" className="text-xs text-destructive">
            {error}
          </p>
        )}
      </div>

      {armed && (
        <div className="flex items-start gap-2">
          <Checkbox
            id={`${ids}-armed`}
            checked={value.confirm_ads_armed}
            onCheckedChange={(checked) =>
              onChange({ ...value, confirm_ads_armed: checked === true })
            }
          />
          <Label htmlFor={`${ids}-armed`} className="text-xs leading-snug">
            This site has a Google Ads account. Linking it turns on live budget pacing. I
            confirm.
          </Label>
        </div>
      )}

      <div className="space-y-1">
        <Label htmlFor={`${ids}-services`} className="text-xs text-muted-foreground">
          Services *
        </Label>
        <Select
          value={value.services ?? ""}
          onValueChange={(v) =>
            onChange({ ...value, services: v as NewClientFieldsValue["services"] })
          }
        >
          <SelectTrigger id={`${ids}-services`} aria-label="Services" className="h-9">
            <SelectValue placeholder="Choose SEO, Ads or Both" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="seo">SEO</SelectItem>
            <SelectItem value="ads">Ads</SelectItem>
            <SelectItem value="both">Both</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1">
        <p id={`${ids}-approval`} className="text-xs text-muted-foreground">
          Blogs need client approval before publishing? *
        </p>
        <RadioGroup
          aria-labelledby={`${ids}-approval`}
          className="flex gap-4"
          value={approvalValue}
          onValueChange={(v) => onChange({ ...value, approval_required: v === "yes" })}
        >
          <div className="flex items-center gap-1.5">
            <RadioGroupItem value="yes" id={`${ids}-approval-yes`} />
            <Label htmlFor={`${ids}-approval-yes`}>Yes</Label>
          </div>
          <div className="flex items-center gap-1.5">
            <RadioGroupItem value="no" id={`${ids}-approval-no`} />
            <Label htmlFor={`${ids}-approval-no`}>No</Label>
          </div>
        </RadioGroup>
      </div>

      {membersSlot}
    </div>
  );
}
